import DeleteModal from "components/DeleteModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { deleteCivilCaseEvidence } from "services/civilCaseComplaintDetailService";
import { CivilCaseComplaint, CivilCaseEvidence, FILE_URL } from "types";
import { errorHandler } from "utils/helper";
import CivilCaseEvidenceForm from "../model/CivilCaseEvidenceForm";
import DocumentViewerModal from "components/DocumentViewer";

type Props = {
  compliant: CivilCaseComplaint;
  setRefetch: any;
  canUpdate: boolean;
};

const CivilCaseEvidenceTab: React.FC<Props> = ({
  compliant,
  setRefetch,
  canUpdate,
}) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedEvidence, setSelectedEvidence] =
    React.useState<CivilCaseEvidence>();

  const handleOpenForm = () => {
    setIsOpenForm(true);
  };

  const handleCloseForm = (refetch = false) => {
    setIsOpenForm(false);
    if (refetch) setRefetch();
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Evidence</h3>
          <p className="text-sm text-gray-500 mt-1">
            Attach supporting documents and files.
          </p>
        </div>
        {canUpdate && (
          <button
            onClick={() => {
              setAction("Add");
              handleOpenForm();
            }}
            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center shadow-sm hover:shadow"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Evidence
          </button>
        )}
      </div>

      {compliant?.evidence_info && compliant?.evidence_info.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">
            No Evidence Added
          </h4>
          <p className="text-gray-500 mb-4">
            Add supporting documents to strengthen this case
          </p>
          {canUpdate && (
            <button
              onClick={() => {
                setAction("Add");
                handleOpenForm();
              }}
              className="px-4 py-2 text-red-600 hover:text-red-800 font-medium"
            >
              Add your first evidence →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {compliant?.evidence_info?.map((evidence, index) => (
            <div
              key={index}
              className="group border border-gray-200 hover:border-red-300 rounded-xl p-4 transition-all hover:shadow-md bg-white"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium mb-1 line-clamp-2">
                      {evidence?.description || "Untitled Evidence"}
                    </p>
                    <DocumentViewerModal
                      documents={(evidence?.uploaded_files || []) as any}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.CIVIL_EVIDENCE}`}
                      layout="list"
                      // label="Letter Upload"
                      hideEmptyFile={true}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(
                        evidence?.attachment_details?.url,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="View Evidence"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </button>
                  {canUpdate && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setAction("Edit");
                          setSelectedEvidence(evidence);
                          handleOpenForm();
                        }}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit Evidence"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEvidence(evidence);
                          setIsOpenDelete(true);
                        }}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Evidence"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isOpenForm && (
        <CivilCaseEvidenceForm
          action={action}
          complaintId={compliant?.id || ""}
          evidence={action === "Edit" ? selectedEvidence : undefined}
          isOpen={isOpenForm}
          onClose={handleCloseForm}
        />
      )}

      {selectedEvidence && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteCivilCaseEvidence(selectedEvidence)
              .then(() => {
                showTooltip("Evidence successfully deleted!", "success");
                setRefetch();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail || errorHandler(error),
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title="Delete Evidence"
          message={`Are you sure you want to delete this evidence? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default CivilCaseEvidenceTab;
